// NumberAreaManager.ts
import { RouletteScene } from '../scenes/RouletteScene';
import { GameConfig } from '../utils/GameConfig';

export interface SpecialNumber {
	number: number;
	enable: boolean;
}

export class NumberAreaManager {
	private scene: RouletteScene;
	private onNumberClick: (sector: string, numberArea: Phaser.GameObjects.Rectangle | Phaser.GameObjects.Graphics) => void;
	private specialNumber: SpecialNumber;
	private createdElements: Phaser.GameObjects.GameObject[] = []; // Armazenar os elementos criados
	private numberAreaContainer: Phaser.GameObjects.Container;
	private highlightContainer: Phaser.GameObjects.Container;

	constructor(
		scene: RouletteScene,
		centerX: number,
		centerY: number,
		onNumberClick: (sector: string, numberArea: Phaser.GameObjects.Rectangle | Phaser.GameObjects.Graphics) => void,
		specialNumber?: SpecialNumber
	) {
		this.scene = scene;
		this.highlightContainer = this.scene.add
			.container(centerX - 663.5, 69)
			.setScale(0.8)
			.setName('areaHighlight');
		this.numberAreaContainer = this.scene.add
			.container(centerX - 663.5, 69)
			.setScale(0.8)
			.setName('areaNumbers');
		this.onNumberClick = onNumberClick;
		this.specialNumber = specialNumber || { enable: false, number: 0 };
		this.createNumberAreas();
		this.createAreaHighlight();
	}

	// Método responsável por criar as áreas de highlight dos números
	private createAreaHighlight() {
		const areaHighlight = GameConfig.areaHighlight;
		for (let i = 0; i < areaHighlight.length; i++) {
			if (areaHighlight[i].name === 'highlight0') {
				const graphics = this.scene.add.graphics();
				// Cor de preenchimento
				graphics.fillStyle(0xffffff, 0);
				// Ponto central (ajuste conforme necessário)
				const centerX = areaHighlight[i].x;
				const centerY = areaHighlight[i].y;
				// Define os pontos do polígono (formato semelhante ao da imagem)
				const points: any = areaHighlight[i].polygon;
				// Transladar os pontos para o centro desejado
				const translatedPoints = points.map(p => ({
					x: p.x + centerX,
					y: p.y + centerY
				}));
				// Desenha o polígono
				graphics.beginPath();
				graphics.moveTo(translatedPoints[0].x, translatedPoints[0].y);
				translatedPoints.forEach(p => graphics.lineTo(p.x, p.y));
				graphics.closePath();
				graphics.fillPath();
				graphics.strokePath();
				graphics.setAlpha(0.01);
				graphics.setName(areaHighlight[i].name);
				// Add a tween animation to the highlight rectangles
				this.scene.tweens.add({
					targets: [graphics],
					alpha: 0.6,
					duration: 500,
					yoyo: true,
					repeat: -1,
					ease: 'Sine.easeInOut'
				});
				this.highlightContainer.add(graphics);
			} else {
				let r = this.scene.add
					.rectangle(
						areaHighlight[i].x + GameConfig.boardOffSetX + 112,
						areaHighlight[i].y + GameConfig.boardOffSetY + 1 + 68,
						areaHighlight[i].width + 2,
						areaHighlight[i].height + 2
					)
					.setInteractive()
					.setAlpha(0.01);
				r.setStrokeStyle(3, 0xffffff, 1);
				r.setFillStyle(0xffffff, 1);
				r.setName(areaHighlight[i].name);
				// Add a tween animation to the highlight rectangles
				this.scene.tweens.add({
					targets: [r],
					fillAlpha: 0.6,
					duration: 500,
					yoyo: true,
					repeat: -1,
					ease: 'Sine.easeInOut'
				});
				this.highlightContainer.add(r);
			}
		}
	}

	// Método responsável por criar as áreas dos números
	private createNumberAreas() {
		// Limpar os elementos criados anteriormente
		this.clearCreatedElements();

		GameConfig.area.forEach(pos => {
			if (pos.polygon && pos.polygon.length > 0) {
				const graphics = this.scene.add.graphics();

				// Cor de preenchimento
				graphics.fillStyle(0xffffff, 0); // verde escuro

				// Ponto central (ajuste conforme necessário)
				const centerX = pos.x;
				const centerY = pos.y;

				// Define os pontos do polígono (formato semelhante ao da imagem)
				const points = pos.polygon;

				// Transladar os pontos para o centro desejado
				const translatedPoints = points.map(p => ({
					x: p.x + centerX,
					y: p.y + centerY
				}));

				// Area
				const bounds = this.getCenterOfPolygon(translatedPoints);

				// Desenha o polígono
				graphics.beginPath();
				graphics.moveTo(translatedPoints[0].x, translatedPoints[0].y);
				translatedPoints.forEach(p => graphics.lineTo(p.x, p.y));
				graphics.closePath();
				graphics.fillPath();
				graphics.strokePath();
				graphics.setData('center', bounds);
				graphics.setName(pos.name);

				// Criar um objeto Phaser.Geom.Polygon com os pontos
				const polygon = new Phaser.Geom.Polygon(translatedPoints);
				// Tornar a área interativa e adicionar o evento de clique
				graphics.setInteractive(polygon, Phaser.Geom.Polygon.Contains);
				if (this.scene.input) {
					// Mudar cursor e responder ao clique
					graphics.on('pointerout', () => {
						this.scene.input.setDefaultCursor('default');
						//this.hideHighlight();
					});
					graphics.on('pointerover', event => {
						this.scene.input.setDefaultCursor('pointer');
						const num = pos.name.split('b')[1];
						//this.showHighlight(num);
					});
					graphics.on('pointerup', () => {
						this.onNumberClick(pos.name, graphics); // Passa o número e a área interativa no clique
					});
				}
				// Adiciona o númeroArea à lista de elementos criados
				this.createdElements.push(graphics);
				// Add to container
				this.numberAreaContainer.add(graphics);
			} else {
				const numberArea = this.scene.add.rectangle(pos.x, pos.y, pos.width, pos.height, 0xffffff, 0);
				numberArea.setName(pos.name);
				// Adiciona o númeroArea à lista de elementos criados
				this.createdElements.push(numberArea);
				// Tornar a área interativa e adicionar o evento de clique
				numberArea.setInteractive({
					cursor: 'pointer'
				});
				numberArea.on('pointerover', event => {
					const num = pos.name.split('b')[1];
					this.showHighlight(num);
				});
				numberArea.on('pointerout', () => {
					this.hideHighlight();
				});
				numberArea.on('pointerup', () => {
					this.onNumberClick(pos.name, numberArea); // Passa o número e a área interativa no clique
				});
				// Add to container
				this.numberAreaContainer.add(numberArea);
			}
		});
	}

	private getCenterOfPolygon(points: { x: number; y: number }[]): Phaser.Math.Vector2 {
		const sum = points.reduce(
			(acc, p) => {
				acc.x += p.x;
				acc.y += p.y;
				return acc;
			},
			{ x: 0, y: 0 }
		);

		return new Phaser.Math.Vector2(sum.x / points.length, sum.y / points.length);
	}

	// Função para limpar os elementos criados anteriormente
	private clearCreatedElements() {
		// Destruir todos os elementos que foram criados
		this.createdElements.forEach(element => {
			if (element && element.destroy) {
				// Verificar se o elemento tem efeitos de postFX e removê-los
				if (element instanceof Phaser.GameObjects.Rectangle && element.postFX) {
					element.postFX.clear(); // Remove os efeitos de glow
				}
				element.destroy(); // Remove o elemento da cena
			}
		});
		this.createdElements = []; // Reseta o array de elementos criados
	}

	// Enable highlight
	public showHighlight(num) {
		if (num < 37) {
			const b: any = this.highlightContainer.getByName('highlight' + num);
			if (num != 0) {
				b.setAlpha(1);
			} else {
				for (let i = 0; i < GameConfig.areaHighlight.length; i++) {
					if (GameConfig.areaHighlight[i].name === 'highlight0') {
						b.clear();
						// Cor de preenchimento
						b.fillStyle(0xffffff, 1);
						// Ponto central (ajuste conforme necessário)
						const centerX = GameConfig.areaHighlight[i].x;
						const centerY = GameConfig.areaHighlight[i].y;
						// Define os pontos do polígono (formato semelhante ao da imagem)
						const points: any = GameConfig.areaHighlight[i].polygon;
						// Transladar os pontos para o centro desejado
						const translatedPoints = points.map(p => ({
							x: p.x + centerX,
							y: p.y + centerY
						}));
						// Desenha o polígono
						b.beginPath();
						b.moveTo(translatedPoints[0].x, translatedPoints[0].y);
						translatedPoints.forEach(p => b.lineTo(p.x, p.y));
						b.closePath();
						b.fillPath();
						b.strokePath();
					}
				}
			}
		} else if (num === '_1_12') {
			for (let i = 1; i <= 12; i++) {
				const b: any = this.highlightContainer.getByName('highlight' + i);
				b.setAlpha(1);
			}
		} else if (num === '_2_12') {
			for (let i = 13; i <= 24; i++) {
				const b: any = this.highlightContainer.getByName('highlight' + i);
				b.setAlpha(1);
			}
		} else if (num === '_3_12') {
			for (let i = 25; i <= 36; i++) {
				const b: any = this.highlightContainer.getByName('highlight' + i);
				b.setAlpha(1);
			}
		} else if (num === '_1_18') {
			for (let i = 1; i <= 18; i++) {
				const b: any = this.highlightContainer.getByName('highlight' + i);
				b.setAlpha(1);
			}
		} else if (num === '_2_18') {
			for (let i = 19; i <= 36; i++) {
				const b: any = this.highlightContainer.getByName('highlight' + i);
				b.setAlpha(1);
			}
		} else if (num === '_even') {
			for (let i = 1; i <= 36; i++) {
				if (i % 2 === 0) {
					const b: any = this.highlightContainer.getByName('highlight' + i);
					b.setAlpha(1);
				}
			}
		} else if (num === '_odd') {
			for (let i = 1; i <= 36; i++) {
				if (i % 2 !== 0) {
					const b: any = this.highlightContainer.getByName('highlight' + i);
					b.setAlpha(1);
				}
			}
		} else if (num === '_red') {
			for (let i = 0; i < GameConfig.reds.length; i++) {
				const b: any = this.highlightContainer.getByName('highlight' + GameConfig.reds[i]);
				b.setAlpha(1);
			}
		} else if (num === '_dark') {
			for (let i = 0; i < GameConfig.blacks.length; i++) {
				const b: any = this.highlightContainer.getByName('highlight' + GameConfig.blacks[i]);
				b.setAlpha(1);
			}
		} else if (num === '_2_1_1') {
			for (let i = 1; i <= 36; i++) {
				if (i % 3 === 1) {
					const b: any = this.highlightContainer.getByName('highlight' + i);
					b.setAlpha(1);
				}
			}
		} else if (num === '_2_1_2') {
			for (let i = 1; i <= 36; i++) {
				if (i % 3 === 2) {
					const b: any = this.highlightContainer.getByName('highlight' + i);
					b.setAlpha(1);
				}
			}
		} else if (num === '_2_1_3') {
			for (let i = 1; i <= 36; i++) {
				if (i % 3 === 0) {
					const b: any = this.highlightContainer.getByName('highlight' + i);
					b.setAlpha(1);
				}
			}
		} else if (
			num === '_1to6' ||
			num === '_4to9' ||
			num === '_7to12' ||
			num === '_10to15' ||
			num === '_13to18' ||
			num === '_16to21' ||
			num === '_19to24' ||
			num === '_22to27' ||
			num === '_25to30' ||
			num === '_28to33' ||
			num === '_31to36'
		) {
			const fN = Number(num.split('_')[1].split('to')[0]);
			const sN = Number(num.split('to')[1]);
			for (let i = fN; i <= sN; i++) {
				const b: any = this.highlightContainer.getByName('highlight' + i);
				b.setAlpha(1);
			}
		} else {
			const btn2h = num.split('_')[1].split('n');
			for (let i = 0; i < btn2h.length; i++) {
				const b: any = this.highlightContainer.getByName('highlight' + btn2h[i]);

				if (btn2h[i] == 0) {
					for (let i = 0; i < GameConfig.areaHighlight.length; i++) {
						if (GameConfig.areaHighlight[i].name === 'highlight0') {
							b.clear();
							// Cor de preenchimento
							b.fillStyle(0xffffff, 1);
							// Ponto central (ajuste conforme necessário)
							const centerX = GameConfig.areaHighlight[i].x;
							const centerY = GameConfig.areaHighlight[i].y;
							// Define os pontos do polígono (formato semelhante ao da imagem)
							const points: any = GameConfig.areaHighlight[i].polygon;
							// Transladar os pontos para o centro desejado
							const translatedPoints = points.map(p => ({
								x: p.x + centerX,
								y: p.y + centerY
							}));
							// Desenha o polígono
							b.beginPath();
							b.moveTo(translatedPoints[0].x, translatedPoints[0].y);
							translatedPoints.forEach(p => b.lineTo(p.x, p.y));
							b.closePath();
							b.fillPath();
							b.strokePath();
						}
					}
				} else {
					b.setAlpha(1);
				}
			}
		}
	}

	// Disable highlight
	public hideHighlight() {
		this.highlightContainer.getAll().map(obj => {
			if ((obj as Phaser.GameObjects.Graphics).name === 'highlight0') {
				for (let i = 0; i < GameConfig.areaHighlight.length; i++) {
					if (GameConfig.areaHighlight[i].name === 'highlight0') {
						(obj as Phaser.GameObjects.Graphics).clear();
						// Cor de preenchimento
						(obj as Phaser.GameObjects.Graphics).fillStyle(0xffffff, 0);
						// Ponto central (ajuste conforme necessário)
						const centerX = GameConfig.areaHighlight[i].x;
						const centerY = GameConfig.areaHighlight[i].y;
						// Define os pontos do polígono (formato semelhante ao da imagem)
						const points: any = GameConfig.areaHighlight[i].polygon;
						// Transladar os pontos para o centro desejado
						const translatedPoints = points.map(p => ({
							x: p.x + centerX,
							y: p.y + centerY
						}));
						// Desenha o polígono
						(obj as Phaser.GameObjects.Graphics).beginPath();
						(obj as Phaser.GameObjects.Graphics).moveTo(translatedPoints[0].x, translatedPoints[0].y);
						translatedPoints.forEach(p => (obj as Phaser.GameObjects.Graphics).lineTo(p.x, p.y));
						(obj as Phaser.GameObjects.Graphics).closePath();
						(obj as Phaser.GameObjects.Graphics).fillPath();
						(obj as Phaser.GameObjects.Graphics).strokePath();
					}
				}
			} else {
				(obj as Phaser.GameObjects.Graphics).setAlpha(0.01);
			}
			return null;
		});
	}

	// Novo método para definir o número especial e atualizar as áreas
	public setSpecialNumber(number: any | null, enable: boolean = true) {
		let newNumber = parseInt(number, 10);
		// Se 'number' for null, desativa o número especial
		if (number === null) {
			this.specialNumber.enable = false;
			this.specialNumber.number = 0;
		} else {
			// Define o novo número especial
			this.specialNumber.enable = enable;
			this.specialNumber.number = newNumber;
		}

		// Recria as áreas de números com o novo destaque
		this.createNumberAreas();
	}
}
